import React from 'react';

import { Button } from '@/design-system/button';
import { Input, InputRoot } from '@/design-system/input';
import { Popover, PopoverTrigger } from '@/design-system/popover';
import { FilterPopoverContent } from '@/components/FilterPopover';
import { SearchSelect } from '@/design-system/search-select';
import { useSearchableOptions } from '@/hooks/useSearchableOptions';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { searchHubOptions } from '@/services/api/pitstops';

// ── Types ────────────────────────────────────────────────────────────────────

export type DatePreset = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days';

export interface InboundFilterValue {
  hubId: string;
  clientId: string;
  datePreset: DatePreset | '';
  createdFrom: string;
  createdTo: string;
}

export const EMPTY_INBOUND_FILTERS: InboundFilterValue = {
  hubId: '',
  clientId: '',
  datePreset: '',
  createdFrom: '',
  createdTo: '',
};

export const countActiveInboundFilters = (v: InboundFilterValue): number =>
  (v.hubId ? 1 : 0) +
  (v.clientId ? 1 : 0) +
  (v.datePreset || v.createdFrom || v.createdTo ? 1 : 0);

// ── Helpers ──────────────────────────────────────────────────────────────────

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function resolvePresetRange(
  preset: DatePreset | ''
): { from: string; to: string } | null {
  if (!preset) return null;
  const today = startOfLocalDay(new Date());
  let from = today;
  let to = today;
  if (preset === 'yesterday') {
    from = addDays(today, -1);
    to = addDays(today, -1);
  } else if (preset === 'last_7_days') {
    from = addDays(today, -6);
  } else if (preset === 'last_30_days') {
    from = addDays(today, -29);
  }
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

// ── Component ────────────────────────────────────────────────────────────────

interface InboundFilterPanelProps {
  open: boolean;
  /** Controls the Radix popover. Opening seeds the draft in the parent. */
  onOpenChange: (open: boolean) => void;
  /** Number of applied filters — shown as an ink pill on the trigger. */
  activeFilterCount: number;
  value: InboundFilterValue;
  onChange: (next: InboundFilterValue) => void;
  onApply: () => void;
  onReset: () => void;
}

const InboundFilterPanel: React.FC<InboundFilterPanelProps> = ({
  open,
  onOpenChange,
  activeFilterCount,
  value,
  onChange,
  onApply,
  onReset,
}) => {
  const labelCls = 'mb-2 block text-[11px] font-medium text-text-sub-600';

  // Server-side (`?search=`) hub dropdown — fetch on open, debounce keystrokes.
  const hubs = useSearchableOptions(open, searchHubOptions);

  // Client (provider) picker — same paginated hook the shipments filter and
  // packages toolbar use: page-1 fetch on open, 300ms-debounced `?search=`,
  // lazy `loadMore()`, plus an id→name cache so the picked client keeps its
  // label after a narrowing search drops it from the results. Replaces the
  // single fetch-200 lookup, which silently truncated larger client lists.
  const clients = useProvidersDropdown(open);

  const updateDatePreset = (preset: DatePreset) => {
    onChange({
      ...value,
      datePreset: value.datePreset === preset ? '' : preset,
      createdFrom: '',
      createdTo: '',
    });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button tone="neutral" style="stroke" size="sm">
          <i className="ri-equalizer-2-line mr-1.5 text-[14px]" />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-1.5 rounded-md bg-bg-strong-950 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-text-white-0">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <FilterPopoverContent
        align="end"
        className="w-[360px] p-0"
        aria-label="Filter inbound items"
      >
        <div className="flex items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            Filter items
          </p>
          <button
            type="button"
            onClick={onReset}
            className="text-[11px] font-medium text-text-sub-600 transition-colors hover:text-accent"
          >
            Reset
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-4 px-4 py-3">
          {/* Date */}
          <div>
            <p className={labelCls}>Date</p>
            <div className="flex flex-wrap gap-1">
              {DATE_PRESETS.map((p) => {
                const active = value.datePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => updateDatePreset(p.key)}
                    className={`rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors ${
                      active
                        ? 'border-accent text-accent'
                        : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={value.createdFrom}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      createdFrom: e.target.value,
                      datePreset: '',
                    })
                  }
                />
              </InputRoot>
              <span className="flex-shrink-0 text-xs text-text-soft-400">
                to
              </span>
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={value.createdTo}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      createdTo: e.target.value,
                      datePreset: '',
                    })
                  }
                />
              </InputRoot>
            </div>
          </div>

          {/* Hub */}
          <div>
            <p className={labelCls}>Hub</p>
            <SearchSelect
              options={hubs.options.map((h) => ({
                value: h.id,
                label: h.name,
              }))}
              value={value.hubId || undefined}
              selectedLabelFallback={
                value.hubId ? hubs.labelFor(value.hubId) : undefined
              }
              onChange={(v) =>
                onChange({ ...value, hubId: (v as string | undefined) ?? '' })
              }
              onSearchChange={hubs.setSearch}
              disableLocalFilter
              loading={hubs.loading}
              placeholder="All hubs"
              allowClear
              emptyMessage={hubs.error ?? 'No matching hub'}
            />
          </div>

          {/* Client */}
          <div>
            <p className={labelCls}>Client</p>
            <SearchSelect
              options={clients.options.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              value={value.clientId || undefined}
              selectedLabelFallback={
                value.clientId ? clients.labelFor(value.clientId) : undefined
              }
              onChange={(v) =>
                onChange({ ...value, clientId: (v as string | undefined) ?? '' })
              }
              onSearchChange={clients.setSearch}
              disableLocalFilter
              loading={clients.loading}
              hasMore={clients.hasMore}
              onLoadMore={clients.loadMore}
              placeholder="All clients"
              allowClear
              emptyMessage={clients.error ?? 'No matching client'}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={onApply}>
            Apply
          </Button>
        </div>
      </FilterPopoverContent>
    </Popover>
  );
};

export default InboundFilterPanel;
